import React from 'react';
import { Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TimeScale
);

const EventTimeSeriesChart = ({ title, eventSeries, timeRange = "24h", variant = "stacked" }) => {
  // Time range configurations for X-axis (same as TimeSeriesChart)
  const getTimeRangeConfig = (range) => {
    const now = new Date();
    
    // Handle custom range objects
    if (typeof range === 'object' && range.type === 'custom') {
      const fromDate = new Date(range.from.endsWith('Z') ? range.from : range.from + 'Z');
      const toDate = new Date(range.to.endsWith('Z') ? range.to : range.to + 'Z');
      const diffMs = toDate.getTime() - fromDate.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      
      // Configure based on custom range duration
      if (diffDays <= 1) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'hour',
          stepSize: 2,
          displayFormat: 'HH:mm'
        };
      } else if (diffDays <= 7) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'day',
          stepSize: 1,
          displayFormat: 'MMM dd HH:mm'
        };
      } else if (diffDays <= 90) {
        return {
          min: fromDate,
          max: toDate,
          unit: 'day',
          stepSize: Math.ceil(diffDays / 10),
          displayFormat: 'MMM dd'
        };
      } else {
        return {
          min: fromDate,
          max: toDate,
          unit: 'month',
          stepSize: 1,
          displayFormat: 'MMM yyyy'
        };
      }
    }
    
    // Handle preset string ranges
    const configs = {
      '5m': {
        min: new Date(now.getTime() - 5 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 1,
        displayFormat: 'HH:mm:ss'
      },
      '15m': {
        min: new Date(now.getTime() - 15 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 2,
        displayFormat: 'HH:mm'
      },
      '30m': {
        min: new Date(now.getTime() - 30 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 5,
        displayFormat: 'HH:mm'
      },
      '1h': {
        min: new Date(now.getTime() - 1 * 60 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 10,
        displayFormat: 'HH:mm'
      },
      '3h': {
        min: new Date(now.getTime() - 3 * 60 * 60 * 1000),
        max: now,
        unit: 'minute',
        stepSize: 30,
        displayFormat: 'HH:mm'
      },
      '6h': {
        min: new Date(now.getTime() - 6 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 1,
        displayFormat: 'HH:mm'
      },
      '12h': {
        min: new Date(now.getTime() - 12 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 2,
        displayFormat: 'HH:mm'
      },
      '24h': {
        min: new Date(now.getTime() - 24 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 3,
        displayFormat: 'HH:mm'
      },
      '2d': {
        min: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'hour',
        stepSize: 6,
        displayFormat: 'MMM dd HH:mm'
      },
      '7d': {
        min: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 1,
        displayFormat: 'MMM dd'
      },
      '14d': {
        min: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 1,
        displayFormat: 'MMM dd'
      },
      '30d': {
        min: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 3,
        displayFormat: 'MMM dd'
      },
      '90d': {
        min: new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000),
        max: now,
        unit: 'day',
        stepSize: 7,
        displayFormat: 'MMM dd'
      }
    };
    return configs[range] || configs['24h'];
  };

  const timeConfig = getTimeRangeConfig(timeRange);
  
  // Determine if we're in timeline mode
  const isTimelineMode = variant === "timeline";
  
  // Convert event data to scatter plot format
  const chartData = {
    datasets: eventSeries.map((series, index) => ({
      label: series.name,
      data: series.events.map(event => ({
        x: event.x,
        y: isTimelineMode ? 1 + (index * 0.15) : index + 1, // Timeline: all at y=1 with slight offset, Stacked: separate rows
        // Include original event data for tooltip access
        recoveryAction: event.recoveryAction,
        recoveryStatus: event.recoveryStatus,
        subsystem: event.subsystem
      })),
      backgroundColor: series.color,
      borderColor: series.color,
      pointRadius: isTimelineMode ? 3 : 4, // Smaller dots in timeline mode
      pointHoverRadius: isTimelineMode ? 5 : 6,
      showLine: false, // No connecting lines - scatter plot only
    }))
  };
  
  // Plugin to draw vertical lines from dots to x-axis in timeline mode
  const verticalLinesPlugin = {
    id: 'verticalLines',
    afterDatasetsDraw: (chart) => {
      if (!isTimelineMode) return; // Only draw in timeline mode
      
      const ctx = chart.ctx;
      const yScale = chart.scales.y;
      
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        
        if (!meta.hidden && meta.data) {
          meta.data.forEach((point) => {
            const x = point.x;
            const yTop = point.y;
            const yBottom = yScale.getPixelForValue(0.5); // Draw to y=0.5 (bottom of chart area)
            
            ctx.save();
            ctx.globalAlpha = 0.5; // 50% transparency
            ctx.strokeStyle = dataset.borderColor || '#666';
            ctx.lineWidth = 1; // Thin line
            ctx.setLineDash([]); // Solid line
            ctx.beginPath();
            ctx.moveTo(x, yTop);
            ctx.lineTo(x, yBottom);
            ctx.stroke();
            ctx.restore();
          });
        }
      });
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'point',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          title: function(context) {
            const date = new Date(context[0].parsed.x);
            return date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
          },
          label: function(context) {
            const seriesName = context.dataset.label;
            const dataIndex = context.dataIndex;
            const dataset = context.dataset;
            
            // Enhanced tooltip for grouped recovery actions
            if (seriesName === 'RECOVERY ACTION') {
              // Access the original event data that contains recovery action details
              const eventData = dataset.data[dataIndex];
              if (eventData && eventData.recoveryAction && eventData.recoveryStatus) {
                return `${eventData.recoveryAction}: ${eventData.recoveryStatus}`;
              }
              return 'Recovery Action: Unknown';
            }
            
            // Enhanced tooltip for recovery actions (legacy format - keeping for backward compatibility)
            if (seriesName.includes('(SUCCESS)') || seriesName.includes('(FAILURE)')) {
              const status = seriesName.includes('(SUCCESS)') ? 'Success' : 'Failure';
              const action = seriesName.replace(/ \((SUCCESS|FAILURE)\)/, '');
              return `${action}: ${status}`;
            }
            
            // Enhanced tooltip for data stall events
            if (['WWAN', 'RMNET', 'IPA', 'MODEM', 'CELLULAR', 'NETWORK'].includes(seriesName)) {
              return `Data Stall in ${seriesName}`;
            }
            
            // Default tooltip
            return `${seriesName}: Event occurred`;
          }
        }
      },
      verticalLines: verticalLinesPlugin
    },
    scales: {
      x: {
        type: 'time',
        min: timeConfig.min,
        max: timeConfig.max,
        bounds: 'data',
        time: {
          unit: timeConfig.unit,
          stepSize: timeConfig.stepSize,
          displayFormats: {
            minute: timeConfig.displayFormat,
            hour: timeConfig.displayFormat,
            day: timeConfig.displayFormat
          }
        },
        ticks: {
          callback: function(value) {
            const d = new Date(value);
            const fmt = timeConfig.displayFormat;
            if (fmt === 'HH:mm:ss') return d.getUTCHours().toString().padStart(2,'0') + ':' + d.getUTCMinutes().toString().padStart(2,'0') + ':' + d.getUTCSeconds().toString().padStart(2,'0');
            if (fmt === 'HH:mm') return d.getUTCHours().toString().padStart(2,'0') + ':' + d.getUTCMinutes().toString().padStart(2,'0');
            if (fmt === 'MMM dd HH:mm') return d.toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
            if (fmt === 'MMM dd') return d.toLocaleString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' });
            if (fmt === 'MMM yyyy') return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
            return value;
          }
        },
        title: {
          display: true,
          text: 'Time (UTC)'
        },
        grid: {
          color: '#e0e0e0'
        }
      },
      y: {
        type: 'linear',
        min: isTimelineMode ? 0.5 : 0.5,
        max: isTimelineMode ? 1.5 : eventSeries.length + 0.5,
        ticks: {
          stepSize: isTimelineMode ? 1 : 1,
          callback: function(value, index) {
            if (isTimelineMode) {
              // In timeline mode, only show "Event Occurred" at y=1
              if (value === 1) return 'Event Occurred';
              return '';
            } else {
              // In stacked mode, show event type names
              if (value >= 1 && value <= eventSeries.length) {
                return eventSeries[value - 1]?.name || '';
              }
              return '';
            }
          }
        },
        title: {
          display: !isTimelineMode,
          text: 'Event Types'
        },
        grid: {
          color: '#e0e0e0',
          display: function(context) {
            // In timeline mode, only draw grid line at y=1
            if (isTimelineMode) {
              return context.tick && context.tick.value === 1;
            }
            return true;
          }
        }
      }
    }
  };

  return (
    <div className="chart-container-wrapper">
      <h4 className="chart-title">{title}</h4>
      <div className="chart-canvas-container">
        <Scatter data={chartData} options={options} plugins={[verticalLinesPlugin]} />
      </div>
    </div>
  );
};

export default EventTimeSeriesChart;
